/**
 * Работа раздела с черновиком: сохранение, откат, разбор отказов.
 *
 * Объединяет хранилище черновиков (§6) с API и превращает ответы 409 и 422
 * в то, что должен увидеть пользователь: разбор расхождений при одновременном
 * редактировании и понятный текст при отказе удалить связанную запись (§5).
 */

import { useCallback, useState } from 'react';
import type { ModuleName } from '@otc/shared';
import { BlockedError, ConflictError, isEmptyCommit, type Blocked, type Conflict } from '../api/client.js';
import { useCommitModule } from '../api/queries.js';
import { useDrafts } from './drafts.js';

export interface SaveOutcome {
  ok: boolean;
  /** Расхождения версий — показываются в окне разбора. */
  conflicts?: Conflict[];
  /** Отказы по ссылкам — показываются как уведомление с текстом из §5. */
  blocked?: Blocked[];
  error?: string;
  /**
   * Временные ключи новых записей → выданные сервером.
   *
   * Нужно тому, кто после сохранения обращается к только что созданной записи
   * по её настоящему ключу: предложить письмо врачу о новом заказе можно лишь
   * тогда, когда заказ уже существует на сервере и у него есть номер.
   */
  tempIdMap?: Record<string, string>;
}

export function useModule(module: ModuleName) {
  const commit = useCommitModule(module);
  const [conflicts, setConflicts] = useState<Conflict[] | null>(null);

  const dirty = useDrafts((state) => {
    // Подписка на все модули: zustand пересчитает селектор при любом изменении,
    // а `isDirty` дешёвый — сравнение полей десятков записей.
    void state.modules;
    return state.isDirty(module);
  });

  const save = useCallback(async (): Promise<SaveOutcome> => {
    const store = useDrafts.getState();
    const payload = store.buildCommit(module);

    // Пустой пакет не отправляется: нажатие «Сохранить» без правок не должно
    // порождать запрос и запись в журнал изменений.
    if (isEmptyCommit(payload)) return { ok: true };

    try {
      const result = await commit.mutateAsync(payload);
      store.applyCommit(module, result);
      return { ok: true, tempIdMap: result.tempIdMap };
    } catch (error) {
      if (error instanceof ConflictError) {
        setConflicts(error.conflicts);
        return { ok: false, conflicts: error.conflicts };
      }
      if (error instanceof BlockedError) {
        return { ok: false, blocked: error.blocked };
      }
      return { ok: false, error: error instanceof Error ? error.message : 'Не удалось сохранить' };
    }
  }, [commit, module]);

  /**
   * «Оставить моё»: правки переносятся на свежую версию и уходят снова.
   *
   * Без этого выбора отказ по версии был тупиком: «вернуться к своим
   * правкам» лишь закрывало окно, правки оставались несохранёнными, и
   * следующая же попытка упиралась в то же расхождение. С автосохранением
   * это стало заметно: попытки шли одна за другой и не могли пройти.
   */
  const keepMine = useCallback(async (): Promise<SaveOutcome> => {
    const current = conflicts;
    if (!current) return { ok: false, error: 'Расхождений нет' };
    useDrafts.getState().rebase(module, current);
    setConflicts(null);
    return save();
  }, [conflicts, module, save]);

  const discard = useCallback(() => {
    useDrafts.getState().discard(module);
    setConflicts(null);
  }, [module]);

  return {
    dirty,
    saving: commit.isPending,
    save,
    keepMine,
    discard,
    conflicts,
    dismissConflicts: () => setConflicts(null),
  };
}

/** Короткие названия разделов для сообщений — как подписи в меню. */
export const MODULE_LABELS: Record<ModuleName, string> = {
  orders: 'Заказы',
  clinics: 'Клиники',
  doctors: 'Врачи',
  constructions: 'Конструкции',
  employees: 'Сотрудники',
  settings: 'Настройки',
  finance: 'Финансы',
  templates: 'Документы',
};
